import { LifeCycleStage } from '@repo/shared/dtos/organization'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { useEffect, useMemo } from 'react'

import { ResetAndUpdate } from '@/components/ResetAndUpdate'
import { ModalHandler } from '@/components/sudo/organization/OrganizationList'
import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { organizationSchema } from '@/validation-schemas'

export function OrganizationEditor({ modalHandler }: ModalHandler) {
  const { tc } = useT()
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      modalHandler.setOrganization(null)
    }
  }
  return (
    <Dialog
      open={!!modalHandler.organization}
      defaultOpen={!!modalHandler.organization}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {tc('edit', { cfl: true })} {modalHandler.organization?.name}
          </DialogTitle>
        </DialogHeader>
        <OrganizationEditorForm modalHandler={modalHandler} />
      </DialogContent>
    </Dialog>
  )
}

function OrganizationEditorForm({ modalHandler }: ModalHandler) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const defaultValues = useMemo(
    () => ({
      name: modalHandler.organization?.name,
      lifeCycleStage: modalHandler.organization?.lifeCycleStage,
    }),
    [modalHandler.organization],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      if (!modalHandler.organization) return
      void dispatch(
        sudoStoreActions.organization.update({
          organizationId: modalHandler.organization.id,
          fields: { name: values.name, lifeCycleStage: values.lifeCycleStage },
        }),
      )
      modalHandler.setOrganization(null)
    },
    schema: {
      name: organizationSchema.name,
      lifeCycleStage: organizationSchema.lifeCycleStage,
    },
  })

  useEffect(() => {
    if (modalHandler.organization) form.reset()
  }, [modalHandler.organization])

  const handleReset = () => form.reset()
  const handleUpdate = () => submit()

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-4" onKeyDown={submitOnPressMetaEnter}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('name', { cfl: true })}</FormLabel>
              <FormControl>
                <Input placeholder={tc('name', { cfl: true })} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lifeCycleStage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('lifeCycleStage', { cfl: true })}</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  defaultValue={field.value}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={tc('select', { cfl: true })} />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {Object.values(LifeCycleStage).map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {stage}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <ResetAndUpdate onReset={handleReset} onUpdate={handleUpdate} disabled={!isFormValid} />
        </DialogFooter>
      </form>
    </Form>
  )
}
